import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { queryClient } from "..";
import { deliveryApi } from "../api/delivery-api";
import { setDeliveryPageAction, setDeliveryPageByDtoAction, } from "../app/reducers/delivery-slice";
import { endGlobalLoadingAction } from "../app/reducers/loading-slice";
import { store } from "../app/store";
import { Confirm } from "../types/confirm";
import {
    CreateDeliveryPayload,
    RecentLocationByDeliveryPayload,
    SearchDeliveryPageByPeriodPayload,
    UpdateDeliveryPayload,
} from "../types/delivery/delivery-payload";
import { DeliveryGroupType } from "../types/schedule/delivery-group-type";
import { DeliveryAllPayload } from "../types/delivery/delivery-all-payload";
import { convertPayloadToRequest } from "../utils/converter/delivery/delivery-all-request-converter";

export enum DeliveryQueryKey {
    Create = "CreateDelivery",
    CreateBulk = "CreateDeliveryBulk",
    Update = "UpdateDelivery",
    Delete = "DeleteDelivery",
    DeleteAll = "DeleteDeliveriesAll",
    GetById = "GetDeliveryById",
    Page = "DeliveryPage",
    PageAll = "DeliveryPageAll",
    RecentLocationPage = "RecentLocationPage",
    DeleteMultiple = "DeleteDeliveriesMultiple",
}

export const useCreateDeliveryMutation = () => {
    return useMutation(
        DeliveryQueryKey.Create,
        (payload: CreateDeliveryPayload) => deliveryApi.create(payload),
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries(DeliveryQueryKey.GetById);
                toast.success(Confirm.Confirm);
            },
        }
    );
};

export const useCreateDeliveryBulkMutation = () => {
    return useMutation(
        DeliveryQueryKey.CreateBulk,
        (payload: CreateDeliveryPayload[]) => deliveryApi.createBulk(payload),
        {
            onSuccess: ({ data }) => {
                queryClient.invalidateQueries(DeliveryQueryKey.Page);
                toast.success(Confirm.Confirm);
            },
            onError: (error) => {
                store.dispatch(endGlobalLoadingAction());
            },
        }
    );
};

export const useUpdateDeliveryMutation = () => {
    return useMutation(
        DeliveryQueryKey.Update,
        (params: { id: string; payload: UpdateDeliveryPayload }) => {
            return deliveryApi.update(params.id, params.payload);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(DeliveryQueryKey.GetById);
                toast.success(Confirm.Update);
            },
        }
    );
};

export const useDeleteDeliveryMutation = (showToast: boolean = true, autoInvalidate: boolean = true) => {
    return useMutation(
        DeliveryQueryKey.Delete,
        (id: string) => deliveryApi.delete(id),
        {
            onSuccess: () => {
                if (showToast) {
                    toast.success(Confirm.Delete);
                }
                if (autoInvalidate) {
                    queryClient.invalidateQueries(DeliveryQueryKey.Page);
                }
            },
        }
    );
};

export const useDeleteDeliveriesAllMutation = () => {
    return useMutation(
        DeliveryQueryKey.Delete,
        (specificDate: string) => deliveryApi.deleteAll(specificDate),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(DeliveryQueryKey.Page);
                toast.success(Confirm.Delete);
            },
        }
    );
};

export const useGetDeliveryByIdQuery = (payload: string) => {
    return useQuery(
        [DeliveryQueryKey.GetById, payload],
        () => deliveryApi.getById(payload),
        {
            enabled: !!payload,
            onSuccess: ({ data }) => {
                const deliveryDto = data;
                store.dispatch(
                    setDeliveryPageByDtoAction({
                        deliveryDto,
                    })
                );
                store.dispatch(endGlobalLoadingAction());
            },
            onError: () => {
                store.dispatch(endGlobalLoadingAction());
            }
        }
    );
};

export const useGetDeliveryPageByPeriodQuery = (
    payload: SearchDeliveryPageByPeriodPayload
) => {
    return useQuery(
        [DeliveryQueryKey.Page, payload],
        () => deliveryApi.search(payload),
        {
            enabled: payload.hasOwnProperty("page"),
            onSuccess: ({ data }) => {
                const deliveryPageDto = data;
                store.dispatch(
                    setDeliveryPageAction({
                        deliveryPageDto,
                    })
                );
                store.dispatch(endGlobalLoadingAction());
            },
            onError: () => {
                store.dispatch(endGlobalLoadingAction());
            }
        }
    );
};

export const useGetDeliveryPageAllBySpecificDateQuery = (
    payload: DeliveryAllPayload,
) => {
    const request = convertPayloadToRequest(payload);

    return useQuery(
        [DeliveryQueryKey.PageAll, request],
        () => deliveryApi.searchAll(request),
        {
            enabled: payload.specificDate !== "",
            onSuccess: ({ data }) => {
                const deliveryPageDto = data;
                store.dispatch(
                    setDeliveryPageAction({
                        deliveryPageDto,
                    })
                );
            },
        }
    );
};

export const useGetDeliveryRecentLocationPageQuery = (params: {
    payload: RecentLocationByDeliveryPayload;
    type: DeliveryGroupType;
}) => {
    return useQuery(
        [DeliveryQueryKey.RecentLocationPage, params.payload],
        () =>
            params.type === DeliveryGroupType.PickUp
                ? deliveryApi.searchRecentPickUpLocationByDelivery(params.payload)
                : deliveryApi.searchRecentDropOffLocationByDelivery(params.payload),
        {
            enabled:
                !!params && !!params.payload?.clientId && !!params.payload?.keyword,
        }
    );
};

export const useDeleteDeliveriesMutation = () => {
    return useMutation(
        DeliveryQueryKey.DeleteMultiple,
        async (ids: string[]) => {
            for (const id of ids) {
                await deliveryApi.delete(id);  // 각 요청이 완료될 때까지 기다림
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(DeliveryQueryKey.Page);
                toast.success("선택한 배송이 삭제되었습니다.");
            },
        }
    );
};
